import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Table,
    Tag,
    Empty,
    Input,
    Select,
    DatePicker,
    Modal,
    Descriptions,
    Checkbox,
    Button as AntButton,
} from "antd";
import {
    FilterOutlined,
    CaretUpOutlined,
    CaretDownOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import CircularProgress from "@mui/material/CircularProgress";
import Swal from "sweetalert2";
import { getBizID, getServices, getStyle } from "../../libs/todoSlice";
import { translateMessage } from "../../lib/errorKeyNote";

const { RangePicker } = DatePicker;

const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

const STATUS_META = {
    pending: { label: "รออนุมัติ", color: "#faad14", tagColor: "warning" },
    approved: { label: "อนุมัติแล้ว", color: "#52c41a", tagColor: "success" },
    rejected: { label: "ปฏิเสธ", color: "#f5222d", tagColor: "error" },
};

const STATUS_SELECT_OPTIONS = [
    { value: "all", label: "ทั้งหมด" },
    { value: "pending", label: STATUS_META.pending.label },
    { value: "approved", label: STATUS_META.approved.label },
    { value: "rejected", label: STATUS_META.rejected.label },
];

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const flattenMessage = (msg) => {
    const translated = translateMessage(msg);
    if (!translated) return "";
    if (typeof translated === "string") return translated;
    if (Array.isArray(translated)) return translated.join(", ");
    if (typeof translated === "object") {
        return Object.values(translated)
            .map((v) => (Array.isArray(v) ? v[0] : v))
            .join(", ");
    }
    return "";
};

function ApprovePreRegister() {
    const params = useParams();
    const navigate = useNavigate();
    const businessId = useSelector(getBizID);
    const style = useSelector(getStyle);
    const services = useSelector(getServices);

    const hasCaService = services.includes("ca");
    const hasRaService = services.includes("one-id-ra");
    const hasPmucService = services.includes("employee-pre-register-pmuc");

    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [actioningCode, setActioningCode] = useState(null);
    const [caRequestingCode, setCaRequestingCode] = useState(null);

    // search
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");

    // advanced filter
    const [filterOpen, setFilterOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [dateRange, setDateRange] = useState(null); // [dayjs, dayjs] | null

    // edit modal (approve/reject)
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editAction, setEditAction] = useState("approve");
    const [caRequest, setCaRequest] = useState(false);
    const [oneIdRa, setOneIdRa] = useState(false);
    const [deniedReason, setDeniedReason] = useState("");
    const [subOrganization, setSubOrganization] = useState("");
    const [address, setAddress] = useState("");
    const [bizEmail, setBizEmail] = useState("");

    // view modal
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingRecord, setViewingRecord] = useState(null);

    const getRecordCode = (record) => record._id;

    // debounce search input -> search
    useEffect(() => {
        const t = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [searchInput]);

    const hasActiveFilter = statusFilter !== "all" || !!dateRange;

    const fetchList = useCallback(async () => {
        if (!businessId) return;
        setLoading(true);
        try {
            const queryParams = { page, limit: PAGE_SIZE };
            if (search) queryParams.search = search;
            if (statusFilter !== "all") queryParams.status = statusFilter;
            if (dateRange && dateRange[0] && dateRange[1]) {
                queryParams.start_date = dateRange[0].format("YYYY-MM-DD");
                queryParams.end_date = dateRange[1].format("YYYY-MM-DD");
            }

            const { data } = await axios({
                method: "GET",
                url: `${process.env.REACT_APP_BACKEND_URL}/api/v1/employee-register/${businessId}`,
                params: queryParams,
                withCredentials: true,
            });
            setRows(Array.isArray(data?.data?.data) ? data.data.data : []);
            setTotal(data?.data?.total ?? 0);
        } catch ({ response }) {
            if (response?.status === 401) {
                Swal.fire({
                    title: "เซสชันหมดอายุ",
                    icon: "warning",
                    text: "กรุณาเข้าสู่ระบบใหม่อีกครั้ง",
                    timer: 2000,
                    showConfirmButton: false,
                });
                navigate(`/${params.prefix}`);
            } else {
                Swal.fire({
                    title: "โหลดข้อมูลไม่สำเร็จ",
                    icon: "error",
                    text: response?.data?.message || "",
                });
            }
        } finally {
            setLoading(false);
        }
    }, [businessId, page, search, statusFilter, dateRange, navigate, params.prefix]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    useEffect(() => {
        if (!hasCaService && caRequest) {
            setCaRequest(false);
        }
    }, [hasCaService, caRequest]);

    useEffect(() => {
        if (!hasRaService && oneIdRa) {
            setOneIdRa(false);
        }
    }, [hasRaService, oneIdRa]);

    const handleClearFilter = () => {
        setStatusFilter("all");
        setDateRange(null);
        setPage(1);
    };

    const openEditModal = (record) => {
        setEditingRecord(record);
        setEditAction("approve");
        setCaRequest(!!record.caRequest);
        setOneIdRa(false);
        setDeniedReason("");
        setSubOrganization(record.subOrganization || "");
        setAddress(record.address || "");
        setBizEmail(record.bizEmail || "");
        setEditModalOpen(true);
    };

    const openViewModal = (record) => {
        setViewingRecord(record);
        setViewModalOpen(true);
    };

    const handleConfirmAction = async () => {
        const record = editingRecord;
        const code = getRecordCode(record);
        if (!code) return;
        if (editAction === "reject" && !deniedReason.trim()) {
            Swal.fire({
                title: "กรุณาระบุเหตุผล",
                icon: "warning",
                text: "โปรดกรอกเหตุผลในการปฏิเสธก่อนยืนยัน",
            });
            return;
        }

        if (
            editAction === "approve" &&
            caRequest &&
            (!subOrganization.trim() || !address.trim() || !bizEmail.trim() || !EMAIL_REGEX.test(bizEmail))
        ) {
            Swal.fire({
                title: "กรอกข้อมูลไม่ครบถ้วน",
                icon: "warning",
                text: "กรุณากรอกชื่อหน่วยงานย่อย ที่อยู่ และอีเมลองค์กรให้ครบถ้วนก่อนยืนยัน",
            });
            return;
        }

        setActioningCode(code);
        try {
            await axios({
                method: "PATCH",
                url: `${process.env.REACT_APP_BACKEND_URL}/api/v1/employee-register/${businessId}/${code}/${editAction}`,
                data:
                    editAction === "approve"
                        ? {
                            ca_request: caRequest,
                            one_id_ra: oneIdRa,
                            // 👇 ส่งเฉพาะตอนขอ CA
                            ...(caRequest && {
                                sub_organization: subOrganization.trim(),
                                address: address.trim(),
                                biz_email: bizEmail.trim(),
                            }),
                        }
                        : { denied_reason: deniedReason.trim() },
                withCredentials: true,
            });
            setEditModalOpen(false);
            Swal.fire({
                title: editAction === "approve" ? "อนุมัติสำเร็จ" : "ปฏิเสธสำเร็จ",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            });
            
            if (editAction === "approve" && caRequest) {
                await handleRequestCa(record);
            } else {
                fetchList();
            }
        } catch ({ response }) {
            if (response?.status === 401) {
                navigate(`/${params.prefix}`);
            } else if (response?.status === 400) {
                setEditModalOpen(false);
                Swal.fire({
                    title: "ไม่สามารถดำเนินการได้",
                    icon: "warning",
                    text:
                        flattenMessage(response?.data?.message) ||
                        "รายการนี้ถูกดำเนินการไปแล้ว หรือไม่อยู่ในสถานะรออนุมัติ",
                });
                fetchList();
            } else if (response?.status === 404) {
                setEditModalOpen(false);
                Swal.fire({
                    title: "ไม่พบรายการ",
                    icon: "error",
                    text:
                        flattenMessage(response?.data?.message) ||
                        "ไม่พบรายการนี้ในระบบ อาจถูกลบหรือย้ายไปแล้ว",
                });
                fetchList();
            } else {
                Swal.fire({
                    title: "ดำเนินการไม่สำเร็จ",
                    icon: "error",
                    text: flattenMessage(response?.data?.message) || "",
                });
            }
        } finally {
            setActioningCode(null);
        }
    }

    const handleRequestCa = async (record) => {
        const code = getRecordCode(record);
        if (!code) return;

        setCaRequestingCode(code);
        try {
            await axios({
                method: "POST",
                url: `${process.env.REACT_APP_BACKEND_URL}/api/v1/employee-register/${businessId}/${code}/ca`,
                withCredentials: true,
            });
            Swal.fire({
                title: "ขอใบรับรองสำเร็จ",
                icon: "success",
                timer: 1500,
                showConfirmButton: false,
            });
            fetchList();
        } catch ({ response }) {
            if (response?.status === 401) {
                navigate(`/${params.prefix}`);
            } else if (response?.status === 404) {
                Swal.fire({
                    title: "ไม่พบรายการ",
                    icon: "error",
                    text: response?.data?.message || "ไม่พบรายการนี้ในระบบ อาจถูกลบหรือย้ายไปแล้ว",
                });
                fetchList();
            } else if (response?.status === 400) {
                Swal.fire({
                    title: "ไม่สามารถขอใบรับรองได้",
                    icon: "warning",
                    text: response?.data?.message || "กรุณาตรวจสอบข้อมูลอีกครั้ง",
                });
                fetchList();
            } else {
                Swal.fire({
                    title: "ขอใบรับรองไม่สำเร็จ",
                    icon: "error",
                    text: response?.data?.message || "",
                });
            }
        } finally {
            setCaRequestingCode(null);
        }
    };

    const columns = useMemo(() => {
        // column แผนก (business ปกติ) / หน่วยงานต้นสังกัด (pmuc) อยู่ตำแหน่งเดียวกัน
        const orgColumn = hasPmucService
            ? {
                title: "หน่วยงานต้นสังกัด",
                dataIndex: "subOrganization",
                key: "subOrganization",
                render: (val) => <Typography variant="body2">{val || "-"}</Typography>,
            }
            : {
                title: "แผนก",
                dataIndex: "department",
                key: "department",
                render: (val) => <Typography variant="body2">{val || "-"}</Typography>,
            };

        return [
            {
                title: "ผู้ลงทะเบียน",
                key: "person",
                render: (_, r) => (
                    <Stack>
                        <Typography variant="body2" fontWeight={600}>
                            {`${r.titleTH || ""}${r.firstNameTH || ""} ${r.lastNameTH || ""}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {`${r.firstNameENG || ""} ${r.lastNameENG || ""}`}
                        </Typography>
                    </Stack>
                ),
            },
            {
                title: "เลขบัตรประชาชน",
                dataIndex: "idCardNum",
                key: "idCardNum",
                render: (val) => (
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {val || "-"}
                    </Typography>
                ),
            },
            {
                title: "เบอร์โทร",
                dataIndex: "mobileNO",
                key: "mobileNO",
                render: (val) => <Typography variant="body2">{val || "-"}</Typography>,
            },
            {
                title: "อีเมล",
                dataIndex: "email",
                key: "email",
                render: (val) => <Typography variant="body2">{val || "-"}</Typography>,
            },
            orgColumn,
            {
                title: "ตำแหน่ง",
                dataIndex: "position",
                key: "position",
                render: (val) => <Typography variant="body2">{val || "-"}</Typography>,
            },
            {
                title: "สถานะ",
                key: "status",
                render: (_, r) => (
                    <Tag color={STATUS_META[r.status]?.tagColor}>
                        {STATUS_META[r.status]?.label || r.status}
                    </Tag>
                ),
            },
            {
                title: "วันที่ลงทะเบียน",
                dataIndex: "createdAt",
                key: "createdAt",
                render: (val) => (val ? new Date(val).toLocaleDateString("th-TH") : "-"),
            },
            {
                title: "จัดการ",
                key: "actions",
                fixed: "right",
                render: (_, r) => (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="ดูรายละเอียด">
                            <IconButton size="small" onClick={() => openViewModal(r)}>
                                <VisibilityOutlinedIcon fontSize="small" sx={{ color: "#666" }} />
                            </IconButton>
                        </Tooltip>
                        {r.status === "pending" && (
                            <Tooltip title="พิจารณาอนุมัติ / ปฏิเสธ">
                                <IconButton
                                    size="small"
                                    disabled={actioningCode === getRecordCode(r)}
                                    onClick={() => openEditModal(r)}
                                >
                                    <EditOutlinedIcon fontSize="small" sx={{ color: "#053075" }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {hasCaService && r.status === "approved" && r.caRequest && r.caStatus !== "created" && (
                            <Tooltip title="ขอใบรับรองอิเล็กทรอนิกส์ (CA)">
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={caRequestingCode === getRecordCode(r)}
                                        onClick={() => handleRequestCa(r)}
                                    >
                                        {caRequestingCode === getRecordCode(r) ? (
                                            <CircularProgress size={16} sx={{ color: "#faad14" }} />
                                        ) : (
                                            <PublishedWithChangesIcon fontSize="small" sx={{ color: "#faad14" }} />
                                        )}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                ),
            },
        ];
    }, [actioningCode, caRequestingCode, hasPmucService, hasCaService]);

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                border: "1px solid #eee",
                maxWidth: "100%",
                overflowX: "hidden",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <PersonOutlineIcon sx={{ color: style.primary_color || "#053075" }} />
                <Typography variant="h6" fontWeight={700}>
                    อนุมัติผู้ลงทะเบียน
                </Typography>
                <Tag color="blue" style={{ marginLeft: 8 }}>
                    ทั้งหมด {total} รายการ
                </Tag>
            </Stack>

            {/* search + filter toggle */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
                <Input.Group compact style={{ display: "flex", flex: 1 }}>
                    <Input
                        allowClear
                        placeholder="ค้นหาชื่อ-สกุล, เลขบัตรประชาชน, เบอร์โทร, อีเมล"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        size="large"
                        style={{ width: "calc(100% - 46px)" }}
                    />
                    <AntButton
                        icon={<SearchOutlined />}
                        size="large"
                        style={{ borderRadius: "0px 4px 4px 0px" }}
                    />
                </Input.Group>
                <AntButton
                    onClick={() => setFilterOpen((v) => !v)}
                    size="large"
                    style={{
                        borderColor: hasActiveFilter ? "#74c24d" : undefined,
                        color: hasActiveFilter ? "#74c24d" : undefined,
                    }}
                >
                    <span
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                        }}
                    >
                        <FilterOutlined />
                        ตัวกรองขั้นสูง
                        {filterOpen ? <CaretUpOutlined /> : <CaretDownOutlined />}
                    </span>
                </AntButton>
            </Stack>

            {filterOpen && (
                <div
                    style={{
                        background: "#fafafa",
                        borderRadius: 12,
                        padding: 20,
                        marginBottom: 20,
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                            columnGap: 24,
                            rowGap: 16,
                            alignItems: "flex-end",
                        }}
                    >
                        <div>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                สถานะ
                            </Typography>
                            <Select
                                value={statusFilter}
                                onChange={(v) => {
                                    setStatusFilter(v);
                                    setPage(1);
                                }}
                                options={STATUS_SELECT_OPTIONS}
                                style={{ width: "100%" }}
                            />
                        </div>

                        <div>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                ช่วงวันที่
                            </Typography>
                            <RangePicker
                                value={dateRange}
                                onChange={(v) => {
                                    setDateRange(v);
                                    setPage(1);
                                }}
                                format="DD/MM/YYYY"
                                placeholder={["วันเริ่มต้น", "วันสิ้นสุด"]}
                                style={{ width: "100%" }}
                            />
                        </div>

                        <div style={{ display: "flex", alignItems: "flex-end" }}>
                            <AntButton danger onClick={handleClearFilter}>
                                ล้างตัวกรอง
                            </AntButton>
                        </div>
                    </div>
                </div>
            )}

            <Table
                rowKey={getRecordCode}
                columns={columns}
                dataSource={rows}
                loading={loading}
                scroll={{ x: "max-content" }}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="ไม่พบรายการ"
                        />
                    ),
                }}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    onChange: (p) => setPage(p),
                    showTotal: (t) => `ทั้งหมด ${t} รายการ`,
                }}
                rowClassName={() => "approve-table-row"}
            />

            {/* Edit (approve/reject) modal */}
            <Modal
                title="พิจารณาอนุมัติผู้ลงทะเบียน"
                open={editModalOpen}
                onCancel={() => setEditModalOpen(false)}
                onOk={handleConfirmAction}
                okText="ยืนยัน"
                cancelText="ยกเลิก"
                confirmLoading={actioningCode === getRecordCode(editingRecord || {})}
                okButtonProps={{
                    disabled:
                        (editAction === "reject" && !deniedReason.trim()) ||
                        (editAction === "approve" &&
                            caRequest &&
                            (!subOrganization.trim() || !address.trim() || !bizEmail.trim() ||
                                !EMAIL_REGEX.test(bizEmail))),
                    style: {
                        backgroundColor: style.primary_color || "#053075",
                        borderColor: style.primary_color || "#053075",
                    },
                }}
            >
                {editingRecord && (
                    <Stack spacing={2}>
                        <Typography variant="body1" fontWeight={600}>
                            {`${editingRecord.titleTH || ""}${editingRecord.firstNameTH || ""} ${editingRecord.lastNameTH || ""}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {`เลขบัตรประชาชน: ${editingRecord.idCardNum || "-"}`}
                        </Typography>

                        <Descriptions bordered column={2} size="small">
                            <Descriptions.Item label="รหัสพนักงาน">
                                {editingRecord.employeeID || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Username">
                                {editingRecord.username || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="แผนก">
                                {editingRecord.department || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="ตำแหน่ง">
                                {editingRecord.position || "-"}
                            </Descriptions.Item>
                            {hasPmucService && (
                                <Descriptions.Item label="สถานะในการทำสัญญากับ บพข." span={2}>
                                    {editingRecord.rolePmuc || "-"}
                                </Descriptions.Item>
                            )}
                        </Descriptions>

                        <div>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                เลือกสถานะ
                            </Typography>
                            <Select
                                value={editAction}
                                onChange={(v) => {
                                    setEditAction(v);
                                    if (v !== "approve") {
                                        setCaRequest(false);
                                        setOneIdRa(false);
                                    } else {
                                        setDeniedReason("");
                                    }
                                }}
                                style={{ width: "100%" }}
                                options={[
                                    { value: "approve", label: "อนุมัติ" },
                                    { value: "reject", label: "ปฏิเสธ" },
                                ]}
                            />
                            {editAction === "approve" && (
                                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                                    {hasRaService && (
                                        <div>
                                            <Checkbox
                                                checked={oneIdRa}
                                                onChange={(e) => setOneIdRa(e.target.checked)}
                                            >
                                                ส่งอีเมลแจ้งผู้ดูแลระบบ (RA)
                                            </Checkbox>
                                        </div>
                                    )}
                                    {hasCaService && (
                                        <div>
                                            <Checkbox
                                                checked={caRequest}
                                                onChange={(e) => setCaRequest(e.target.checked)}
                                            >
                                                ขอออกใบรับรองอิเล็กทรอนิกส์ (CA)
                                            </Checkbox>
                                        </div>
                                    )}

                                    {hasCaService && caRequest && (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 4 }}>
                                            <div>
                                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                                    ชื่อของหน่วยงาน
                                                </Typography>
                                                <Input
                                                    value={subOrganization}
                                                    onChange={(e) => setSubOrganization(e.target.value)}
                                                    placeholder="ชื่อของหน่วยงาน"
                                                    status={!subOrganization.trim() ? "error" : ""}
                                                    maxLength={200}
                                                />
                                                {!subOrganization.trim() && (
                                                    <Typography variant="caption" sx={{ color: "#f5222d" }}>
                                                        กรุณากรอกชื่อของหน่วยงาน
                                                    </Typography>
                                                )}
                                            </div>

                                            <div>
                                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                                    ที่อยู่
                                                </Typography>
                                                <Input
                                                    value={address}
                                                    onChange={(e) => setAddress(e.target.value)}
                                                    placeholder="ที่อยู่"
                                                    status={!address.trim() ? "error" : ""}
                                                    maxLength={300}
                                                />
                                                {!address.trim() && (
                                                    <Typography variant="caption" sx={{ color: "#f5222d" }}>
                                                        กรุณากรอกที่อยู่
                                                    </Typography>
                                                )}
                                            </div>

                                            <div>
                                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                                    อีเมลองค์กร
                                                </Typography>
                                                <Input
                                                    value={bizEmail}
                                                    onChange={(e) => setBizEmail(e.target.value)}
                                                    placeholder="อีเมลองค์กร"
                                                    status={!bizEmail.trim() || !EMAIL_REGEX.test(bizEmail) ? "error" : ""}
                                                    maxLength={100}
                                                />
                                                {!bizEmail.trim() ? (
                                                    <Typography variant="caption" sx={{ color: "#f5222d" }}>
                                                        กรุณากรอกอีเมลองค์กร
                                                    </Typography>
                                                ) : !EMAIL_REGEX.test(bizEmail) ? (
                                                    <Typography variant="caption" sx={{ color: "#f5222d" }}>
                                                        รูปแบบอีเมลไม่ถูกต้อง
                                                    </Typography>
                                                ) : null}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            {editAction === "reject" && (
                                <div style={{ marginTop: 12, marginBottom: 12 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                                        เหตุผลในการปฏิเสธ
                                    </Typography>
                                    <Input.TextArea
                                        value={deniedReason}
                                        onChange={(e) => setDeniedReason(e.target.value)}
                                        placeholder="ระบุเหตุผลที่ปฏิเสธการลงทะเบียนนี้"
                                        rows={3}
                                        maxLength={100}
                                        showCount
                                        style={{ resize: "none" }}
                                    />
                                </div>
                            )}
                        </div>
                    </Stack>
                )}
            </Modal>

            {/* View detail modal */}
            <Modal
                title="รายละเอียดผู้ลงทะเบียน"
                open={viewModalOpen}
                onCancel={() => setViewModalOpen(false)}
                footer={null}
                width={700}
            >
                {viewingRecord && (
                    <Descriptions bordered column={2} size="small">
                        <Descriptions.Item label="ชื่อ (ไทย)" span={2}>
                            {`${viewingRecord.titleTH || ""}${viewingRecord.firstNameTH || ""} ${viewingRecord.lastNameTH || ""}`}
                        </Descriptions.Item>
                        <Descriptions.Item label="ชื่อ (อังกฤษ)" span={2}>
                            {`${viewingRecord.titleENG || ""} ${viewingRecord.firstNameENG || ""} ${viewingRecord.lastNameENG || ""}`}
                        </Descriptions.Item>
                        <Descriptions.Item label="คำนำหน้าพิเศษ (ไทย)">
                            {viewingRecord.specialTitleTH || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="คำนำหน้าพิเศษ (อังกฤษ)">
                            {viewingRecord.specialTitleENG || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="เลขบัตรประชาชน" span={2}>
                            {viewingRecord.idCardNum || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="รหัสพนักงาน">
                            {viewingRecord.employeeID || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Username">
                            {viewingRecord.username || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="แผนก">
                            {viewingRecord.department || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="ตำแหน่ง">
                            {viewingRecord.position || "-"}
                        </Descriptions.Item>
                        {hasPmucService && (
                            <Descriptions.Item label="สถานะในการทำสัญญากับ บพข." span={2}>
                                {viewingRecord.rolePmuc || "-"}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="เบอร์โทร">
                            {viewingRecord.tel || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="เบอร์มือถือ">
                            {viewingRecord.mobileNO || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="อีเมล">
                            {viewingRecord.email || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="อีเมลองค์กร">
                            {viewingRecord.bizEmail || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="ขอใบรับรองอิเล็กทรอนิกส์ (CA)">
                            {viewingRecord.caRequest ? "ใช่" : "ไม่ใช่"}
                        </Descriptions.Item>
                        <Descriptions.Item label="ส่งอีเมลแจ้งผู้ดูแลระบบ (RA)">
                            {viewingRecord.status === "approved"
                                ? (viewingRecord.oneIdRa ? "ใช่" : "ไม่ใช่")
                                : "-"}
                        </Descriptions.Item>
                        {viewingRecord.subOrganization && (
                            <Descriptions.Item label="หน่วยงานต้นสังกัด" span={2}>
                                {viewingRecord.subOrganization}
                            </Descriptions.Item>
                        )}
                        {viewingRecord.address && (
                            <Descriptions.Item label="ที่อยู่" span={2}>
                                {viewingRecord.address}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="สถานะ">
                            <Tag color={STATUS_META[viewingRecord.status]?.tagColor}>
                                {STATUS_META[viewingRecord.status]?.label || viewingRecord.status}
                            </Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="วันที่ลงทะเบียน">
                            {viewingRecord.createdAt
                                ? new Date(viewingRecord.createdAt).toLocaleString("th-TH")
                                : "-"}
                        </Descriptions.Item>
                        {viewingRecord.status === "rejected" && viewingRecord.deniedReason && (
                            <Descriptions.Item label="เหตุผลที่ปฏิเสธ" span={2}>
                                {viewingRecord.deniedReason}
                            </Descriptions.Item>
                        )}
                        <Descriptions.Item label="แก้ไขล่าสุด" span={2}>
                            {viewingRecord.updatedAt
                                ? new Date(viewingRecord.updatedAt).toLocaleString("th-TH")
                                : "-"}
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>
        </Paper>
    );
}

export default ApprovePreRegister;